<template lang="pug">
.monitor-viewer-cam-view
  .cam-view-header(v-if='!isFullscreen')
    .header-left
      ui-button(@click='goBack', icon='el-icon-arrow-left') 返回监控列表
      h1 {{ camInfo.title }}
    .header-right
      ui-button(@click='toggleFullscreen', type='primary') 全屏查看
      ui-button(@click='goBack', type='text') 关闭

  .cam-view-container(:class='{ fullscreen: isFullscreen }')
    .cam-player-container
      cam-view(
        v-if='camInfo',
        :cam='camInfo',
        :hide-controls='isFullscreen',
        :compact='isFullscreen',
        layout='col',
        ref='camView'
      )

    .cam-controls(v-if='!isFullscreen')
      .control-section
        h3 监控信息
        .info-item
          label 设备名称：
          span {{ camInfo.title }}
        .info-item
          label 设备状态：
          span(:class='{ online: camInfo.state, offline: !camInfo.state }')
            | {{ camInfo.state ? '在线' : '离线' }}

    .fullscreen-controls(v-if='isFullscreen')
      .fullscreen-header
        h2 {{ camInfo.title }}
        .fullscreen-actions
          ui-button(@click='toggleFullscreen', icon='el-icon-close') 退出全屏
          ui-button(@click='goBack', type='text') 返回列表
</template>

<script>
import { _ } from '@yishitec/web';
import CamView from '../../components/admin/CamView/CamView.vue';

export default {
  name: 'MonitorViewerCamView',
  components: { CamView },
  data() {
    return {
      camId: null,
      camInfo: null,
      isFullscreen: false,
      loading: false,
    };
  },
  async mounted() {
    this.camId = this.$route.params.camId;
    await this.loadCamInfo();
    this.setupFullscreenListeners();
  },
  beforeDestroy() {
    this.removeFullscreenListeners();
  },
  methods: {
    async loadCamInfo() {
      try {
        this.loading = true;
        // 这里需要根据camId获取监控信息
        // 由于API设计，我们需要从项目列表中查找
        const projects = await this.api('/api/monitor-viewer/projects')();
        let found = false;

        for (const project of projects) {
          try {
            const cams = await this.api(`/api/monitor-viewer/cams/${project.id}`)();
            const cam = cams.find((c) => c.id == this.camId);
            if (cam) {
              this.camInfo = { ...cam, project_id: project.id };
              found = true;
              break;
            }
          } catch (e) {
            // 继续查找下一个项目
          }
        }

        if (!found) {
          throw new Error('未找到指定的监控设备');
        }
      } catch (e) {
        this.$message.error('获取监控信息失败');
        this.goBack();
      } finally {
        this.loading = false;
      }
    },

    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }
    },

    enterFullscreen() {
      const element = this.$el;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
    },

    exitFullscreen() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    },

    setupFullscreenListeners() {
      this.fullscreenChangeHandler = () => {
        this.isFullscreen = !!(
          document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
        );
      };

      document.addEventListener('fullscreenchange', this.fullscreenChangeHandler);
      document.addEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
      document.addEventListener('mozfullscreenchange', this.fullscreenChangeHandler);
      document.addEventListener('MSFullscreenChange', this.fullscreenChangeHandler);
    },

    removeFullscreenListeners() {
      if (this.fullscreenChangeHandler) {
        document.removeEventListener('fullscreenchange', this.fullscreenChangeHandler);
        document.removeEventListener('webkitfullscreenchange', this.fullscreenChangeHandler);
        document.removeEventListener('mozfullscreenchange', this.fullscreenChangeHandler);
        document.removeEventListener('MSFullscreenChange', this.fullscreenChangeHandler);
      }
    },

    goBack() {
      // 需要获取项目ID，从camInfo中获取或从路由参数中获取
      if (this.camInfo && this.camInfo.project_id) {
        this.$router.push(`/monitor-viewer/project/${this.camInfo.project_id}/cams`);
      } else {
        // 如果没有项目信息，返回仪表板
        this.$router.push('/monitor-viewer');
      }
    },
  },
};
</script>

<style lang="less" scoped>
.monitor-viewer-cam-view {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;

  &.fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 9999;
    background: #000;
  }
}

.cam-view-header {
  background: white;
  border-radius: 8px;
  padding: 15px 20px;
  margin: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .header-left {
    display: flex;
    align-items: center;
    gap: 15px;

    h1 {
      margin: 0;
      color: #333;
      font-size: 18px;
    }
  }
}

.cam-view-container {
  flex: 1;
  display: flex;
  gap: 20px;
  padding: 0 20px 20px;

  &.fullscreen {
    padding: 0;
    gap: 0;
    background: #000;
  }
}

.cam-player-container {
  flex: 1;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  .fullscreen & {
    border-radius: 0;
    box-shadow: none;
    background: #000;
  }
}

.cam-controls {
  width: 300px;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: fit-content;

  h3 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 16px;
  }
}

.control-section {
  .info-item {
    display: flex;
    margin-bottom: 10px;

    label {
      width: 80px;
      color: #666;
      font-size: 14px;
    }

    span {
      color: #333;
      font-size: 14px;

      &.online {
        color: #67c23a;
      }

      &.offline {
        color: #f56c6c;
      }
    }
  }
}

.fullscreen-controls {
  position: absolute;
  top: 20px;
  right: 20px;
  z-index: 1000;

  .fullscreen-header {
    display: flex;
    align-items: center;
    gap: 15px;

    h2 {
      margin: 0;
      color: white;
      font-size: 18px;
    }
  }

  .fullscreen-actions {
    display: flex;
    gap: 10px;
  }
}

// 全屏模式下的样式调整
.fullscreen {
  .cam-view-container {
    position: relative;
  }

  .cam-player-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}
</style>
